import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion } from 'framer-motion';
import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { MenuDropdownSeparator } from '~/components/header/Menu/BaseMenuDropdown';
import { AccountDisplayWithMembership } from '~/components/ui/AccountDisplay';
import { AccountSelectMenuContent, AccountSelectMenuItem } from '~/components/ui/AccountSelectMenu';
import { UserAvatar } from '~/components/ui/UserAvatar';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { chatStore } from '~/lib/stores/chat';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { userStore } from '~/lib/stores/user';
import { AccountSelectedTrigger, AnalyticsAction, AnalyticsTrackEvent, CreateTeamTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import type { UserData } from '~/types/user';
import { dropdownAnimationVariants } from '~/utils/animationVariants';

interface AccountSelectorMenuProps {
  user: UserData;
  activeOrganization: OrganizationData | null;
  side?: 'right' | 'left' | 'bottom';
  align?: 'start' | 'end';
}

export const AccountSelectorMenu = memo(
  ({ user, activeOrganization, side = 'right', align = 'end' }: AccountSelectorMenuProps) => {
    const selectableOrgs = useStore(userStore.selectableOrganizations);

    const onSelectAccount = async (organization: OrganizationData | null) => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.AccountSelected,
          properties: {
            from: AccountSelectedTrigger.Menu,
          },
        },
      });
      userStore.setActiveOrganization(organization);

      try {
        await chatStore.loadChatList();
      } catch {
        toast.error('Failed to load your chats');
      }
    };

    const createTeam = useCallback(() => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.CreateTeamMenuClicked,
          properties: {
            from: CreateTeamTrigger.Menu,
          },
        },
      });

      teamPricingPlansStore.startCreateTeamFlow();
    }, []);

    return (
      <DropdownMenu.Portal>
        <AccountSelectMenuContent side={side} align={align} asChild>
          <motion.div initial="close" animate="open" variants={dropdownAnimationVariants}>
            {activeOrganization && (
              <AccountSelectMenuItem onClick={() => onSelectAccount(activeOrganization)}>
                <AccountDisplayWithMembership
                  type="organization"
                  user={user}
                  accountName={activeOrganization.name}
                  membershipLevel={user.membership?.tier}
                  avatar={activeOrganization?.avatar}
                />
              </AccountSelectMenuItem>
            )}

            <AccountSelectMenuItem onClick={() => onSelectAccount(null)}>
              <AccountDisplayWithMembership
                type="user"
                accountName={user.name ?? user.username}
                membershipLevel={user.membership?.tier}
                avatar={user.avatar}
              />
            </AccountSelectMenuItem>

            {selectableOrgs.map((organization) => (
              <AccountSelectMenuItem key={organization.id} onClick={() => onSelectAccount(organization)}>
                <AccountDisplayWithMembership
                  type="organization"
                  user={user}
                  accountName={organization?.name}
                  membershipLevel={organization.billing?.tier}
                  avatar={organization?.avatar}
                />
              </AccountSelectMenuItem>
            ))}

            <MenuDropdownSeparator />
            <AccountSelectMenuItem onClick={createTeam}>
              <UserAvatar iconClassName="i-ph:users-three" backgroundColor="bg-background" />
              <div>Create a team</div>
            </AccountSelectMenuItem>
          </motion.div>
        </AccountSelectMenuContent>
      </DropdownMenu.Portal>
    );
  },
);
